import { UserManagerSettings } from 'oidc-client-ts';

const INAPPBROWSER_TARGET = '_blank';
const REDIRECT_OPTIONS = 'location=no,toolbar=no,zoom=no,clearcache=no,clearsessioncache=no';
const IFRAME_OPTIONS = 'hidden=yes,clearcache=no,clearsessioncache=no';
const APP_SCHEME = 'app';
const HTTPS_SCHEME = 'https';

const { origin } = window.location;
const isAppScheme = origin.startsWith(APP_SCHEME);
const httpsOrigin = isAppScheme ? origin.replace(APP_SCHEME, HTTPS_SCHEME) : origin;

const appRedirect = `${httpsOrigin}/index.html`;
const silentRedirect = `${httpsOrigin}/signincallback.html`;
const logoutRedirect = `${appRedirect}`;

export const cordovaOidcSettings: Partial<UserManagerSettings> = {
  redirect_uri: appRedirect,
  silent_redirect_uri: silentRedirect,
  popup_redirect_uri: silentRedirect,
  post_logout_redirect_uri: logoutRedirect,
  fetchRequestCredentials: 'include',
};

const isAppRedirectUrl = (href: string) => href.startsWith(appRedirect) || href.startsWith(silentRedirect);
const replaceToAppUrl = (url: string) => (isAppScheme ? url.replace(HTTPS_SCHEME, APP_SCHEME) : url);

const InAppBrowserRedirectHandler = (() => {
  // InAppBrowser page, if exists
  let isIFrame: boolean = false;
  let browser;
  let reject: (reason: Error) => void;
  let resolve: (item: { url: string }) => void;

  // close InAppBrowser page if exists
  const close = () => {
    if (browser) {
      browser.removeEventListener('loadstart', onLoadStartListener);
      browser.removeEventListener('loaderror', onError);
      browser.close();
      browser = undefined;
    }
  };

  // handle skip redirect, close inappbrowser
  const handleSkipRedirect = (url: string) => {
    close();
    resolve!({ url });
  };

  // redirect to the application with params and hash, should set the url, and reload the application
  const handleArrivedBackToApp = (url: string) => {
    close();
    resolve!({ url });
    // skip logout url
    const usedUrl = replaceToAppUrl(url);
    // set the application redirect url
    window.location.href = usedUrl;
    // on devices the window.location.href changes are blocked by router
    // reload application
    window.location.reload();
  };

  // if starts with the application url, can redirect to application
  const isBackToAppUrl = (url: string) => isAppRedirectUrl(url);

  const checkAndHandleRedirectUrl = (url: string) => {
    if (isBackToAppUrl(url)) {
      if (isIFrame) {
        handleSkipRedirect(url);
      } else {
        handleArrivedBackToApp(url);
      }
    }
  };

  // handle InAppBrowser page loaded, and check if redirected back to the application
  const onLoadStartListener = async ({ url }) => {
    checkAndHandleRedirectUrl(url);
  };

  const onError = () => {
    close();
  };

  const abort = (err: Error) => {
    close();
    reject(err);
  };

  // open new InAppBrowser page if not contains app url, or open the url in app view
  // if skip redirect than only wait until arrived back to localhost, but skip reload application on redirect url (for example on iframe window)
  const open = (url: string, pIsIFrame: boolean) => {
    isIFrame = pIsIFrame;
    const isBackToApp = isBackToAppUrl(url);
    const promise = new Promise((presolve, preject) => {
      reject = preject;
      resolve = presolve;
    });
    if (!isBackToApp) {
      close();
      browser = window.cordova!.InAppBrowser!.open(
        url,
        INAPPBROWSER_TARGET,
        isIFrame ? IFRAME_OPTIONS : REDIRECT_OPTIONS,
      );
      browser.addEventListener('loadstart', onLoadStartListener);
    } else {
      handleArrivedBackToApp(url);
    }
    return promise;
  };

  return {
    open,
    abort,
  };
})();

class InAppBrowserNavigator {
  private isIFrame: boolean;

  constructor(pisIFrame: boolean) {
    this.isIFrame = pisIFrame;
  }

  /**
   * Returns oidc-client-ts Promise<IWindow>, but interface not public
   */
  public async prepare() {
    return {
      navigate: async (params): Promise<any> => {
        return InAppBrowserRedirectHandler.open(params.url, this.isIFrame);
      },
      close: () => {
        InAppBrowserRedirectHandler.abort(new Error('InAppBrowserNavigator redirect aborted'));
      },
    };
  }
}

export default InAppBrowserNavigator;
